import React, { useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Checkbox from '@mui/material/Checkbox'
import StatusCell from "./StatusCell"
import EditPaymentModal from "../../../../components/PaymentModals/EditPaymentModal"
import ConfirmModal from "../../../../components/ConfirmModal/ConfirmModal"
import { changePaymentsProcess } from "../../../../features/payment/paymentSlice"
import { describeParkedCharge, formatGatewayResponse, isNeverRetry, isParkedCharge } from "../../../../utils/gatewayResponse"
import { useDispatch, useSelector } from 'react-redux'
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import { formatMoney } from "../../../../utils/amount"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import { convertServerToLocalTime, formatDateField } from "../../../../utils/date"
import PaymentPopover from "./PaymentPopover"
import GatewayPicker from "../../../../components/GatewayPicker/GatewayPicker"
import { useIsSupervisor } from "../../../../hooks/user"
import { PAYMENT_SCHEDULE } from "../../../../config/consts"
import { LuTrash2 } from "react-icons/lu"
import { handleTableId } from "../../../../helpers/helper"
import { computeOwed, isPartiallyCollected } from "../../../../utils/partialPayment"
import { selectFeatureFlag } from "../../../../features/featureFlags/selectors"
import { PARKED_CHARGE_VISIBLE } from "../../../../config/featureFlagKeys"
import ProcessPaymentConfirm from "../../../../components/PaymentModals/ProcessPaymentConfirm"
import { useUiKit } from "../../../../components/UiKit/UiKitContext"
import { failureOf } from "../../../../utils/errorHelpers"

const headerCellSx = {
  fontSize: '13px',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: 'text.secondary',
  backgroundColor: 'var(--color-bg-subtle)',
  borderBottom: 1,
  borderColor: 'divider',
  px: '10px',
  py: 0,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  lineHeight: 1,
}

const bodyCellSx = {
  fontSize: '0.875rem',
  fontWeight: 400,
  borderBottom: 1,
  borderColor: 'divider',
  color: 'text.primary',
  px: '10px',
  py: 0,
  verticalAlign: 'middle',
}

// WS 2074858. A retry carries its attempt; the first attempt carries none, so it is "Attempt 1"
// once something points at it. Null for a payment that was never rescheduled.
const attemptOf = (row, rows = []) => {
  if (row?.attempt) return row.attempt
  return rows?.some((other) => other?.rescheduledFrom === row?.id) ? 1 : null
}

const CHARGE_NAME_ACCOUNT_TYPES = ['Credit Card', 'Affiliate']

const chargeNameOf = (row, lsaChargeName) => (
  CHARGE_NAME_ACCOUNT_TYPES.includes(row?.billingAccount) ? lsaChargeName?.ofRecord?.label || '' : ''
)

const TableList = ({ triggerUpdate = false, curUserId, data, setPaymentId, paymentId, userRole, lsaChargeName }) => {
  const dispatch = useDispatch()
  const showChargeAs = !!(lsaChargeName?.ofRecord || lsaChargeName?.required)
  const isSupervisor = useIsSupervisor()
  const { confirm: kitConfirm } = useUiKit()
  const parkedChargeVisible = useSelector(selectFeatureFlag(PARKED_CHARGE_VISIBLE))
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [openId, setOpenId] = React.useState(null)
  const handleClick = (event, id) => {
    setOpenId(id)
    setPaymentId(id)
    setAnchorEl(event.currentTarget)
  }
  const { triggerLoading } = useMyContextLoading()

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const showActions = userRole === 'customer' || isSupervisor
  const [selected, setSelected] = useState([])

  const formatBillingAccount = ({ isBankAccount, billingAccount, billingAccountLast4 }) => {
    if (isBankAccount) {
      if (billingAccount === 'Bank Account') {
        return `ACH **${billingAccountLast4} `
      } else if (billingAccount === 'Credit Card') {
        return `CC **${billingAccountLast4}`
      }
    } else {
      return "Missing"
    }
  }

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(data.map((row) => row.id))
    } else {
      setSelected([])
    }
  }

  const handleSelectRow = (rowId) => {
    setSelected((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    )
  }

  const [openEdit, setOpenEdit] = useState(false)
  const [openChangeStatus, setOpenChangeStatus] = useState(false)
  // Per-charge gateway pick from the dialog's picker — override for THIS charge only, and
  // never carried to another payment: the picker clears it whenever it loads a new one, and
  // Confirm stays out of reach until this payment's own options have arrived.
  const [selectedGateway, setSelectedGateway] = useState(null)
  const [gatewayOptionsLoading, setGatewayOptionsLoading] = useState(true)

  const trigger = () => {
    triggerUpdate(true)
    setAnchorEl(null)
  }

  const processPayment = async () => {
    triggerLoading(true)
    try {
      let result = await dispatch(changePaymentsProcess({ paymentId: openId, ...(selectedGateway || {}) }))
      if (result.error) {
        const errorMessage = result?.payload || 'An error occurred. Please try again.'
        showSnackBar("error", errorMessage)
      } else if (result?.payload?.data?._id) {
        showSnackBar("success", "The payment is being processed")
      }
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred. Please try again.')
    }
    triggerLoading(false)
  }

  const processPaymentOrThrow = async () => {
    const result = await dispatch(changePaymentsProcess({ paymentId: openId, ...(selectedGateway || {}) }))
    if (result.error) {
      trigger()
      throw failureOf(result, 'An error occurred. Please try again.')
    }
    if (result?.payload?.data?._id) showSnackBar("success", "The payment is being processed")
    trigger()
  }

  const payment = data?.find(el => el.id === paymentId)

  return (
    <div>
      {openChangeStatus && kitConfirm && (
        <ProcessPaymentConfirm
          open={openChangeStatus}
          onClose={() => setOpenChangeStatus(false)}
          amount={formatMoney(computeOwed(payment))}
          methodType={payment?.billingAccount}
          last4={payment?.billingAccountLast4}
          confirmDisabled={gatewayOptionsLoading}
          onConfirm={processPaymentOrThrow}
        >
          <GatewayPicker paymentId={openId} onChange={setSelectedGateway} onLoadingChange={setGatewayOptionsLoading} />
          {payment?.amountPaid > 0 && (
            <div className="text-text-secondary text-xs">
              Scheduled {formatMoney(payment.amount)} — {formatMoney(payment.amountPaid)} already collected
            </div>
          )}
        </ProcessPaymentConfirm>
      )}
      {openChangeStatus && !kitConfirm && <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />} title="Are you sure to process the Payment?"
        disabled={gatewayOptionsLoading}
        modalButtonTitle={"Confirm"} classes="" open={openChangeStatus}
        setOpen={setOpenChangeStatus} functionButton={processPayment}>
        <div>
          You will not be able to cancel the processing after you confirm this alert
        </div>
        <br />
        <div>
          Billing Type: {payment?.billingAccount}
        </div>
        <div>
          {payment?.billingAccount === 'Bank Account' ? 'Account' : 'Card'} Number: {payment?.billingAccountLast4}
        </div>
        <br />
        <GatewayPicker paymentId={openId} onChange={setSelectedGateway} onLoadingChange={setGatewayOptionsLoading} />
        <div>
          Amount: {formatMoney(computeOwed(payment))}
        </div>
        {payment?.amountPaid > 0 && (
          <div className="text-text-secondary text-xs">
            Scheduled {formatMoney(payment.amount)} — {formatMoney(payment.amountPaid)} already collected
          </div>
        )}
      </ConfirmModal>}
      {openEdit && <EditPaymentModal triggerUpdate={trigger} curUserId={curUserId}
        dataRow={data?.find((item) => item?.id === openId)} open={openEdit}
        onClose={setOpenEdit} idPayment={openId} />}

      {(!data || data.length === 0) ? (
        <div className="flex items-center justify-center min-h-[300px]"
          style={{ color: 'var(--color-gray-500)', fontSize: 14 }}>
          No rows
        </div>
      ) : (
        <TableContainer className="rounded-xl border border-divider" sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small" sx={{ border: 'none' }}>
            <TableHead>
              <TableRow sx={{ height: 56 }}>
                {showActions && (
                  <TableCell sx={{ ...headerCellSx, width: 50, px: 0, textAlign: 'center' }} padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < (data?.length || 0)}
                      checked={data?.length > 0 && selected.length === data?.length}
                      onChange={handleSelectAll}
                      sx={{ color: 'text.secondary' }}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Date Due</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 75 }}>Pmt#</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 120 }}>Amount</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 220 }}>Gateway Response</TableCell>
                <TableCell sx={headerCellSx}>Frequency</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 200 }}>Transaction ID</TableCell>
                <TableCell sx={headerCellSx}>Billing Account</TableCell>
                {showChargeAs && <TableCell sx={headerCellSx}>Charge as</TableCell>}
                <TableCell sx={headerCellSx}>Created</TableCell>
                <TableCell sx={headerCellSx}>Created By</TableCell>
                {showActions && (
                  <TableCell sx={{ ...headerCellSx, width: 70 }}>Action</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => {
                const isBankAccount = !!row.billingAccount
                const parked = parkedChargeVisible && isParkedCharge(row?.transactionData, row?.statusObj?.value)
                return (
                  <TableRow
                    key={row.id}
                    hover
                    className={row.status === 'Failed' ? 'bg-error-light' : ''}
                    sx={{ height: 75, '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    {showActions && (
                      <TableCell sx={{ ...bodyCellSx, width: 50, px: 0, textAlign: 'center' }} padding="checkbox">
                        <Checkbox
                          checked={selected.includes(row.id)}
                          onChange={() => handleSelectRow(row.id)}
                          sx={{ color: 'text.secondary' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={bodyCellSx}>{formatDateField(row.dueDate)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {row.pmt}
                      {/* WS 2074858: a rescheduled declined payment keeps its number; name the attempt. */}
                      {attemptOf(row, data) && (
                        <div className="text-xs text-text-secondary whitespace-nowrap" data-testid={`attempt-${row.id}`}>
                          Attempt {attemptOf(row, data)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{formatMoney(row.amount)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <StatusCell props={{ row, formattedValue: row.status }} />
                      {parked && (
                        <span
                          className="mt-1 inline-block px-2 py-0.5 rounded-full bg-warning-light text-warning-main text-xs"
                          title="The last charge attempt was not confirmed. Check it with the gateway before charging again."
                          data-testid={`parked-charge-${row.id}`}
                        >
                          Needs Review
                        </span>
                      )}
                      {row?.settledByPayment && (
                        <span
                          className="mt-1 inline-block px-2 py-0.5 rounded-full bg-success-light text-success-main text-xs"
                          title={`Settled later by payment ${handleTableId(row?.settledByPayment)}`}
                        >
                          Settled later
                        </span>
                      )}
                      {isPartiallyCollected({ ...row, status: row?.statusObj?.value }) && (
                        <>
                          <span
                            className="mt-1 inline-block px-2 py-0.5 rounded-full bg-warning-light text-warning-main text-xs"
                            title={`${formatMoney(row.amountPaid)} already collected by a payment link`}
                          >
                            Partially paid
                          </span>
                          <div className="text-text-secondary text-xs">
                            {formatMoney(computeOwed(row))} of {formatMoney(row.amount)}
                          </div>
                        </>
                      )}
                    </TableCell>
                    {/* What the gateway actually said. Same source as the Transactions tab, so the
                        two can never disagree. A dash means no gateway was ever called (a failure
                        marked by hand), which is different from "the gateway said nothing". */}
                    <TableCell sx={bodyCellSx} data-testid={`gateway-response-${row.id}`}>
                      {(() => {
                        const { headline, detail } = parked
                          ? describeParkedCharge(row?.transactionData)
                          : formatGatewayResponse(row?.transactionData)
                        const danger = isNeverRetry(row?.transactionData)
                        return (
                          <div title={detail ? `${headline}\n${detail}` : headline}>
                            <div
                              className={`font-bold line-clamp-2 ${danger ? 'text-error-main' : ''}`}
                            >
                              {headline}
                            </div>
                            {detail && (
                              <p className="text-xs text-text-secondary line-clamp-2">{detail}</p>
                            )}
                            {danger && (
                              <span className="mt-1 inline-block px-2 py-0.5 rounded-full bg-error-light text-error-main text-xs">
                                Do not retry
                              </span>
                            )}
                          </div>
                        )
                      })()}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {PAYMENT_SCHEDULE?.find((item) => item.value === row.frequency)?.label || row.type}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <div
                        className="w-full text-wrap line-clamp-3"
                        title={row.transactionId || ''}
                      >
                        {row.transactionId || ''}
                      </div>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <span className={!isBankAccount ? 'text-primary-error-600' : ''}>
                        {formatBillingAccount({
                          isBankAccount,
                          billingAccount: row.billingAccount,
                          billingAccountLast4: row.billingAccountLast4
                        })}
                      </span>
                    </TableCell>
                    {showChargeAs && <TableCell sx={bodyCellSx}>{chargeNameOf(row, lsaChargeName)}</TableCell>}
                    <TableCell sx={bodyCellSx}>{convertServerToLocalTime(row.created)}</TableCell>
                    <TableCell sx={bodyCellSx}>{row.createdBy}</TableCell>
                    {showActions && (
                      <TableCell sx={bodyCellSx}>
                        <div
                          aria-describedby={id}
                          onClick={(event) => handleClick(event, row.id)}
                          className="cursor-pointer inline-flex justify-center"
                        >
                          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z" fill="var(--color-gray-500)" />
                            <path d="M14 5C14 6.10457 13.1046 7 12 7C10.8954 7 10 6.10457 10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5Z" fill="var(--color-gray-500)" />
                            <path d="M14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19Z" fill="var(--color-gray-500)" />
                          </svg>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PaymentPopover paymentId={paymentId}
        data={data}
        anchorEl={anchorEl}
        id={id}
        handleClose={handleClose}
        open={open}
        setOpenChangeStatus={setOpenChangeStatus}
        setOpenEdit={setOpenEdit}
        trigger={trigger} />
    </div>
  )
}

export default TableList
